import { Injectable, inject } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { AuthService, UserService } from "@services";
import { Store } from "@store";
import { Observable, combineLatest, map, take, catchError, of } from "rxjs";
import type { IFuturesNewChallengeOptionConfiguration } from "../../futures/classes/FuturesNewChallengeOptionConfiguration";
import { selectFuturesNewChallenge } from "../../store/futures-new-challenge/futures-new-challenge.selector";

const CREATE_NEW_CHALLENGE_PAGE_URL: string = "/futures/new-challenge";
const ORDER_DETAILS_PAGE_URL: string = "/futures/new-challenge/order-details";

@Injectable({ providedIn: "root" })
export class FuturesPaymentPageGuard implements CanActivate {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);
  private readonly _store: Store = inject(Store);
  private readonly _userService: UserService = inject(UserService);

  public canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this._store.select(selectFuturesNewChallenge),
      this._authService.isAuthenticated(),
      this._userService.isMissingKycData().pipe(
        // Fallback if the request fails because you're not yet logged in. The UrlTree is a truthy
        // value in the "missing KYC data" slot, so for a signed-in user this redirects to
        // order-details, which is the outcome this guard has always had.
        catchError(
          (): Observable<boolean | UrlTree> => of(this._router.parseUrl(ORDER_DETAILS_PAGE_URL)),
        ),
      ),
    ]).pipe(
      take(1),
      map(
        ([selectedChallenge, isAuthenticated, isMissingKycData]: [
          IFuturesNewChallengeOptionConfiguration | null,
          boolean,
          boolean | UrlTree,
        ]): boolean | UrlTree => {
          if (!selectedChallenge) {
            return this._router.parseUrl(CREATE_NEW_CHALLENGE_PAGE_URL);
          }

          if (isAuthenticated && isMissingKycData) {
            return this._router.parseUrl(ORDER_DETAILS_PAGE_URL);
          }

          return true;
        },
      ),
    );
  }
}
